import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { CurrencyIcon, Skeleton } from "../../components/shared";
import {
  DateFilterDrawer,
  DateRange,
  groupByDay,
  HistoryFilterBar,
  HistoryRow,
  STATUSES_BY_GROUP,
  StatusFilterDrawer,
  StatusGroup,
  TypeFilterDrawer,
} from "../../components/pages/history";
import { NetworkPickerDrawer } from "../../components/pages/wallet/NetworkPickerDrawer";
import {
  cryptoNetworkToDepositNetwork,
  DepositNetwork,
} from "../../components/pages/wallet/networks";
import {
  getCryptoOverview,
  getOrCreateCryptoWallet,
  listCryptoWallets,
  provisionCryptoWallets,
  type CryptoOverviewItem,
  type CryptoOverviewNetwork,
  type CryptoWallet,
} from "../../services/crypto";
import { fetchExchangeRate } from "../../services/exchange-rate";
import { getHistory, type HistoryOrderType } from "../../services/orders/history";
import { useTelegram } from "../../hooks";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";
import { CopyIcon } from "../../icons";

/**
 * Страница отдельного крипто-актива: `/asset/USDT`, `/asset/BTC` и т.д.
 *
 * Композиция сверху вниз:
 *   • header  (кнопка «назад» + название актива)
 *   • hero    (крупная иконка, баланс в монетах и ≈ ₽)
 *   • actions (4 квадратные кнопки: Пополнить · Перевести · Обменять · QR)
 *   • wallets (адреса депозита по каждой сети + QR / копия)
 *   • history (фильтры + список сделок по этой валюте)
 *
 * Открывается кликом по любому активу в списке `Мои активы` и в поиске.
 * Если у актива только одна сеть — кнопки «Пополнить»/«Перевести» ведут
 * сразу на `/deposit` (или `/withdraw`), минуя пикер сетей.
 */

/* --------------------------- helpers ---------------------------- */

const DEFAULT_ICONS: Record<string, string> = {
  USDT: "https://static.coincap.io/assets/icons/usdt@2x.png",
  BTC: "https://static.coincap.io/assets/icons/btc@2x.png",
  ETH: "https://static.coincap.io/assets/icons/eth@2x.png",
  SOL: "https://static.coincap.io/assets/icons/sol@2x.png",
  TRX: "https://static.coincap.io/assets/icons/trx@2x.png",
  BNB: "https://static.coincap.io/assets/icons/bnb@2x.png",
  USDC: "https://static.coincap.io/assets/icons/usdc@2x.png",
  LTC: "https://static.coincap.io/assets/icons/ltc@2x.png",
};

const NETWORK_ICONS: Record<string, string> = {
  TRC20: "https://static.coincap.io/assets/icons/trx@2x.png",
  BEP20: "https://static.coincap.io/assets/icons/bnb@2x.png",
  ERC20: "https://static.coincap.io/assets/icons/eth@2x.png",
  BTC: "https://static.coincap.io/assets/icons/btc@2x.png",
  SOL: "https://static.coincap.io/assets/icons/sol@2x.png",
  LTC: "https://static.coincap.io/assets/icons/ltc@2x.png",
};

function formatAmount(v: number, decimals: number): string {
  if (!Number.isFinite(v) || v === 0) return "0";
  return v.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimals, 6),
  });
}

function shortenAddress(a: string): string {
  if (!a || a.length <= 14) return a || "";
  return `${a.slice(0, 8)}…${a.slice(-6)}`;
}

/* --------------------------- QR pieces -------------------------- */

/**
 * QR-картинка через api.qrserver.com. Автоматически показывает
 * spinner, пока картинка грузится, чтобы не «прыгал» layout.
 */
function QrImage({ data, size }: { data: string; size: number }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}&margin=1`;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        padding: "10px",
        borderRadius: "16px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="img"
        src={url}
        alt="QR"
        sx={{ width: "100%", height: "100%", display: "block" }}
      />
    </Box>
  );
}

/**
 * Модалка с большим QR-кодом кошелька. Открывается по клику на кнопку
 * QR в строке сети. Внутри — QR, полный адрес, memo (если есть) и
 * кнопка «Скопировать адрес».
 */
function WalletQrDialog({
  open,
  onClose,
  currency,
  networkName,
  networkStandard,
  address,
  memo,
}: {
  open: boolean;
  onClose: () => void;
  currency: string;
  networkName: string;
  networkStandard: string;
  address: string | null;
  memo: string | null;
}) {
  const { t } = useTranslation("asset");
  const handleCopy = (value: string, label: string) => {
    navigator.clipboard?.writeText(value);
    enqueueSnackbar(t("toasts.copied_label", { label }), { variant: "success" });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Поверх нижней навигации.
      sx={{ zIndex: 100020 }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" } } }}
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          backgroundImage: "none",
          borderRadius: "24px",
          padding: "20px 20px 22px",
          margin: "16px",
          width: "100%",
          maxWidth: "360px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <Typography sx={{ fontSize: "17px", fontWeight: 600 }}>
          {currency} · {networkStandard}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t("actions.close_aria")}
          sx={{ color: "text.secondary" }}
        >
          <Icon icon="solar:close-circle-linear" width={22} height={22} />
        </IconButton>
      </Box>

      <Typography
        sx={{
          fontSize: "12px",
          color: "text.secondary",
          textAlign: "center",
          marginBottom: "14px",
        }}
      >
        {t("wallets.send_only", { currency, network: networkName })}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "18px" }}>
        {address ? <QrImage data={address} size={200} /> : <QrImage data=" " size={200} />}
      </Box>

      <Box
        sx={{
          padding: "12px 14px",
          borderRadius: "14px",
          bgcolor: "secondary.main",
          marginBottom: memo ? "8px" : 0,
        }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary", marginBottom: "4px" }}>
          {t("wallets.address_label")}
        </Typography>
        <ButtonBase
          onClick={() =>
            address && handleCopy(address, t("wallets.address_label"))
          }
          disabled={!address}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            textAlign: "left",
            wordBreak: "break-all",
          }}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 500, flex: 1 }}>
            {address ?? "—"}
          </Typography>
          {address && (
            <Box sx={{ color: "primary.main", display: "flex", flexShrink: 0 }}>
              <CopyIcon />
            </Box>
          )}
        </ButtonBase>
      </Box>

      {memo && (
        <Box
          sx={{
            padding: "12px 14px",
            borderRadius: "14px",
            bgcolor: "secondary.main",
          }}
        >
          <Typography sx={{ fontSize: "11px", color: "text.secondary", marginBottom: "4px" }}>
            Memo / Tag
          </Typography>
          <ButtonBase
            onClick={() => handleCopy(memo, "Memo")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              textAlign: "left",
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 500, flex: 1 }}>
              {memo}
            </Typography>
            <Box sx={{ color: "primary.main", display: "flex", flexShrink: 0 }}>
              <CopyIcon />
            </Box>
          </ButtonBase>
        </Box>
      )}

      <ButtonBase
        onClick={() =>
          address && handleCopy(address, t("wallets.address_label"))
        }
        disabled={!address}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          padding: "12px 16px",
          borderRadius: "40px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: "14px",
          fontWeight: 500,
          marginTop: "14px",
          opacity: !address ? 0.5 : 1,
        }}
      >
        <CopyIcon />
        {t("actions.copy_address")}
      </ButtonBase>
    </Dialog>
  );
}

/* -------------------------- action row -------------------------- */

function AssetActionButton({
  icon,
  label,
  sublabel,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  /** Основной заголовок (первая строка) — действие: «Пополнить», «Вывести»… */
  label: string;
  /**
   * Подпись во второй строке — тикер валюты / уточнение. Если не задано,
   * рендерим только основную строку и высота кнопки не меняется (за счёт
   * пустого блока с той же высотой строки).
   */
  sublabel?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <ButtonBase
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={(theme) => ({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "12px 6px",
        borderRadius: "18px",
        bgcolor: "secondary.main",
        color: "text.primary",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.05)"
            : "none",
        opacity: disabled ? 0.4 : 1,
        transition: "background 0.15s",
        "&:active": {
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.04)",
        },
      })}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "30px", // 2 строки × 15px — держим одинаковую высоту всех кнопок
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            textAlign: "center",
            lineHeight: 1.25,
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        {sublabel && (
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 500,
              textAlign: "center",
              lineHeight: 1.25,
              color: "text.secondary",
              opacity: 0.75,
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              marginTop: "1px",
            }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}

/* --------------------------- wallet row ------------------------- */

interface WalletRowProps {
  currencyCode: string;
  currencyIcon: string | null;
  network: CryptoOverviewNetwork;
  wallet: CryptoWallet | null;
  isCreating: boolean;
  /**
   * Пока идёт первичная загрузка списка кошельков или автосоздание
   * (`provisionCryptoWallets`) — рендерим скелетон вместо кнопки
   * «Получить», иначе на долю секунды мигает промпт «нет адреса».
   * Кошельки создаются на бэке автоматом на онбординге, так что
   * «Получить» это лишь fallback на случай сбоя DV.net.
   */
  isBootstrapping: boolean;
  onOpenQr: () => void;
  onCreate: () => void;
}

function WalletRow({
  currencyCode,
  currencyIcon,
  network,
  wallet,
  isCreating,
  isBootstrapping,
  onOpenQr,
  onCreate,
}: WalletRowProps) {
  const { t } = useTranslation("asset");
  const handleCopy = () => {
    if (!wallet?.address) return;
    navigator.clipboard?.writeText(wallet.address);
    enqueueSnackbar(t("toasts.address_copied"), { variant: "success" });
  };

  const feeText = network.min_withdraw
    ? `${network.min_withdraw} ${currencyCode}`
    : "0";
  const hasAddress = Boolean(wallet?.address);
  const isBootstrappingRow = isBootstrapping && !hasAddress;
  const displayAddress = wallet?.address
    ? shortenAddress(wallet.address)
    : isBootstrappingRow
      ? ""
      : t("wallets.no_address");

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 14px",
        borderRadius: "18px",
        bgcolor: "secondary.main",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.05)"
            : "none",
      })}
    >
      {/* Аватар: иконка валюты + маленький бейджик сети снизу справа */}
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <CurrencyIcon
          value={currencyIcon ?? DEFAULT_ICONS[currencyCode] ?? null}
          size={42}
          fallbackLetter={currencyCode}
          alt={currencyCode}
          sx={(t) => ({
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
          })}
        />
        <Box
          sx={(t) => ({
            position: "absolute",
            right: -2,
            bottom: -2,
            width: 18,
            height: 18,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: t.palette.background.default,
            padding: "1px",
          })}
        >
          <CurrencyIcon
            value={network.icon ?? NETWORK_ICONS[network.code] ?? null}
            size={16}
            fallbackLetter={network.code}
            alt={network.code}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {currencyCode}
          </Typography>
          <Box
            sx={(t) => ({
              fontSize: "10px",
              fontWeight: 700,
              padding: "2px 6px",
              borderRadius: "6px",
              bgcolor:
                t.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.06)",
              color: "text.secondary",
              letterSpacing: "0.02em",
            })}
          >
            {network.code}
          </Box>
        </Box>
        {isBootstrappingRow ? (
          <Skeleton
            variant="rounded"
            width={140}
            height={14}
            sx={{ borderRadius: "6px" }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayAddress}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: "11px",
            color: "text.disabled",
            marginTop: "3px",
          }}
        >
          {t("wallets.network_fee", { fee: feeText })}
        </Typography>
      </Box>

      {isBootstrappingRow ? (
        <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <Skeleton
            variant="rounded"
            width={38}
            height={38}
            sx={{ borderRadius: "12px" }}
          />
          <Skeleton
            variant="rounded"
            width={38}
            height={38}
            sx={{ borderRadius: "12px" }}
          />
        </Box>
      ) : hasAddress ? (
        <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
          <IconButton
            onClick={onOpenQr}
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: "12px",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.04)",
              color: "text.primary",
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            })}
            aria-label={t("actions.show_qr")}
          >
            <Icon icon="solar:qr-code-linear" width={20} height={20} />
          </IconButton>
          <IconButton
            onClick={handleCopy}
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: "12px",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(0, 0, 0, 0.04)",
              color: "text.primary",
              "&:hover": {
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            })}
            aria-label={t("actions.copy_aria")}
          >
            <Icon icon="solar:copy-linear" width={20} height={20} />
          </IconButton>
        </Box>
      ) : (
        <ButtonBase
          disabled={isCreating}
          onClick={onCreate}
          sx={{
            flexShrink: 0,
            padding: "8px 12px",
            borderRadius: "40px",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontSize: "12px",
            fontWeight: 600,
            opacity: isCreating ? 0.7 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            whiteSpace: "nowrap",
          }}
        >
          {isCreating ? (
            <CircularProgress size={14} sx={{ color: "primary.contrastText" }} />
          ) : (
            t("actions.get_address")
          )}
        </ButtonBase>
      )}
    </Box>
  );
}

/* --------------------------- main page -------------------------- */

export function AssetDetailPage() {
  const { code = "" } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const upperCode = code.toUpperCase();
  const { t, i18n } = useTranslation(["asset", "history"]);

  const { tg } = useTelegram();
  const { isFeatureEnabled } = useFeatureFlags();
  // Если админ выключил обмен — кнопка «Обменять» на карточке актива
  // не показывается: там вкладка «Сервисы» и виджет обмена скрыт.
  const exchangeEnabled = isFeatureEnabled("exchange");

  // Нативная telegram-кнопка «назад».
  useEffect(() => {
    const handleBack = () => navigate(-1);
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, tg]);

  /* ------- data ------- */

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryFn: getCryptoOverview,
    queryKey: ["crypto-overview"],
    staleTime: 30_000,
  });

  const asset: CryptoOverviewItem | null = useMemo(() => {
    if (!overview) return null;
    return overview.find((i) => i.code.toUpperCase() === upperCode) ?? null;
  }, [overview, upperCode]);

  const { data: usdtRate } = useQuery({
    queryFn: () =>
      fetchExchangeRate({ fromCurrency: "USDT", toCurrency: "RUB", sellAmount: 1 }),
    queryKey: ["rate", "USDT", "RUB"],
    staleTime: 60_000,
  });
  const usdtRub = Number(usdtRate?.show_rate ?? usdtRate?.rate ?? 0) || 0;
  const rateToRub: Record<string, number> = {
    USDT: usdtRub,
    USDC: usdtRub,
    BTC: usdtRub * 100_000,
    ETH: usdtRub * 3_500,
    SOL: usdtRub * 200,
    BNB: usdtRub * 600,
    LTC: usdtRub * 90,
    TRX: usdtRub * 0.15,
  };

  /* ------- wallets per network -------
   *
   * Для каждой сети текущей валюты пробуем взять уже созданный адрес.
   * `useQueries` — react-query 3 API, каждый запрос независимо кешируется.
   * Мы стартуем их с `enabled: false` и триггерим руками кнопкой «Получить»,
   * чтобы не создавать десяток адресов на разных сетях сразу при заходе
   * на страницу. Но если адрес УЖЕ создан (у react-query есть кеш) —
   * используем его сразу.
   */

  const networks = asset?.networks ?? [];

  const walletQueries = useQueries(
    networks.map((n) => ({
      queryKey: ["crypto-wallet", n.code, upperCode],
      queryFn: () => getOrCreateCryptoWallet(n.code, upperCode),
      // Не тянем автоматически — только через `.refetch()` из «Получить».
      enabled: false,
      staleTime: 60_000 * 30,
      retry: 0,
    })),
  );

  /* ------- catch-up: авто-подтягивание уже созданных адресов -------
   *
   * Бэкенд на `/me` дёргает `provisionIfNeeded` в фоне — но фронт про это
   * не знает, поэтому здесь:
   *   1) тянем `listCryptoWallets` (все адреса юзера) и раскладываем их
   *      по react-query кэшу `["crypto-wallet", network, currency]` —
   *      после этого UI сразу покажет уже созданные адреса вместо
   *      «нажмите получить»;
   *   2) если для текущей валюты каких-то сетей ещё нет — один раз за
   *      сессию дёргаем `POST /me/crypto/wallets/provision` и рефетчим
   *      список. Идемпотентно на бэке (уникальный индекс + skip).
   */
  const allWalletsQuery = useQuery({
    queryKey: ["crypto-wallets-list"],
    queryFn: listCryptoWallets,
    staleTime: 30_000,
  });
  const allWallets = allWalletsQuery.data;

  // Отдельный флаг для авто-provision. Пока идёт первичная загрузка списка
  // кошельков или провижн — прячем кнопку «Получить» и рисуем скелетон,
  // чтобы юзер не подумал, что адрес нужно создавать вручную.
  const [isProvisioning, setIsProvisioning] = useState(false);

  useEffect(() => {
    if (!allWallets || allWallets.length === 0) return;
    for (const w of allWallets) {
      if (w.currency_code.toUpperCase() === upperCode) {
        queryClient.setQueryData(["crypto-wallet", w.network_code, upperCode], w);
      }
    }
  }, [allWallets, upperCode, queryClient]);

  useEffect(() => {
    if (networks.length === 0) return;
    if (!allWallets) return; // ждём первый ответ list-запроса

    const haveForCurrent = new Set(
      allWallets
        .filter((w) => w.currency_code.toUpperCase() === upperCode)
        .map((w) => w.network_code),
    );
    const missing = networks.filter((n) => !haveForCurrent.has(n.code));
    if (missing.length === 0) return;

    const sessionKey = `zero_provision_tried_${upperCode}`;
    if (sessionStorage.getItem(sessionKey)) return;
    sessionStorage.setItem(sessionKey, "1");

    setIsProvisioning(true);
    provisionCryptoWallets()
      .then(() => queryClient.invalidateQueries(["crypto-wallets-list"]))
      .catch(() => {
        // Тихо: если DV.net упал — юзер может нажать «получить» руками.
      })
      .finally(() => setIsProvisioning(false));
  }, [allWallets, networks, upperCode, queryClient]);

  const [creatingNetwork, setCreatingNetwork] = useState<string | null>(null);
  const handleCreate = async (networkCode: string, idx: number) => {
    setCreatingNetwork(networkCode);
    try {
      await walletQueries[idx].refetch();
    } catch (e) {
      const anyErr = e as { response?: { data?: { message?: string } }; message?: string };
      enqueueSnackbar(
        anyErr?.response?.data?.message ??
          anyErr?.message ??
          t("asset:toasts.create_failed"),
        { variant: "error" },
      );
    } finally {
      setCreatingNetwork(null);
    }
  };

  /* ------- QR dialog ------- */

  const [qrOpenFor, setQrOpenFor] = useState<string | null>(null);

  /* ------- deposit / withdraw pickers ------- */

  const [depositPickerOpen, setDepositPickerOpen] = useState(false);
  const [withdrawPickerOpen, setWithdrawPickerOpen] = useState(false);

  const assetNetworks: DepositNetwork[] = useMemo(() => {
    if (!asset) return [];
    return asset.networks.map((n) => cryptoNetworkToDepositNetwork(asset, n));
  }, [asset]);

  const handleDeposit = () => {
    if (!asset) return;
    if (asset.networks.length === 0) return;
    if (asset.networks.length === 1) {
      const n = asset.networks[0];
      navigate(
        `/deposit?network=${encodeURIComponent(n.code)}&currency=${encodeURIComponent(asset.code)}`,
      );
      return;
    }
    setDepositPickerOpen(true);
  };

  const handleWithdraw = () => {
    if (!asset) return;
    if (asset.networks.length === 0) return;
    if (asset.networks.length === 1) {
      const n = asset.networks[0];
      navigate(
        `/withdraw?network=${encodeURIComponent(n.code)}&currency=${encodeURIComponent(asset.code)}`,
      );
      return;
    }
    setWithdrawPickerOpen(true);
  };

  const handleExchange = () => {
    if (!asset) return;
    navigate(`/exchange?currency=${encodeURIComponent(asset.code)}`);
  };

  const handleQr = () => navigate("/pay-qr");

  /* ------- history (filtered by currency) ------- */

  const [typeFilter, setTypeFilter] = useState<HistoryOrderType[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusGroup | null>(null);
  const [dateFilter, setDateFilter] = useState<DateRange | null>(null);
  const [openDrawer, setOpenDrawer] = useState<"date" | "type" | "status" | null>(null);
  const closeDrawer = () => setOpenDrawer(null);

  const statuses = statusFilter ? STATUSES_BY_GROUP[statusFilter] : undefined;

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryFn: () =>
      getHistory({
        page: 1,
        pageSize: 50,
        types: typeFilter.length > 0 ? typeFilter : undefined,
        statuses,
        dateFrom: dateFilter?.from.toISOString(),
        dateTo: dateFilter?.to.toISOString(),
      }),
    queryKey: [
      "history-asset",
      upperCode,
      typeFilter,
      statusFilter,
      dateFilter?.from.toISOString(),
      dateFilter?.to.toISOString(),
    ],
    keepPreviousData: true,
  });

  // Фильтруем на клиенте по валюте. Заявка релевантна, если код есть в
  // sell_currency, buy_currency или fee_currency — так покрываем обмены,
  // депозиты/выводы и служебные операции.
  const filteredHistory = useMemo(() => {
    const all = historyData?.items ?? [];
    return all.filter((o) => {
      const codes = [o.sell_currency, o.buy_currency, o.fee_currency]
        .filter(Boolean)
        .map((c) => (c ?? "").toUpperCase());
      return codes.includes(upperCode);
    });
  }, [historyData, upperCode]);

  const groups = useMemo(() => groupByDay(filteredHistory), [filteredHistory]);

  // Короткое название месяца для лейбла фильтра «Дата: 12 сен» / «Sep 12».
  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "ru-RU";
  const dateLabel = dateFilter
    ? new Intl.DateTimeFormat(dateLocale, {
        day: "numeric",
        month: "short",
      })
        .format(dateFilter.from)
        .replace(".", "")
    : t("asset:history.filter_date");
  const typeLabel =
    typeFilter.length === 0
      ? t("asset:history.filter_type")
      : t("asset:history.filter_type_n", { count: typeFilter.length });
  const statusLabel =
    statusFilter === null
      ? t("asset:history.filter_status")
      : statusFilter === "success"
        ? t("history:statuses.succeeded")
        : statusFilter === "processing"
          ? t("history:statuses.pending")
          : t("history:statuses.failed");
  const hasFilters =
    typeFilter.length > 0 || statusFilter !== null || dateFilter !== null;

  /* ------- render ------- */

  const amount = Number(asset?.balance ?? 0);
  const rate = rateToRub[upperCode] ?? 0;
  const rubValue = amount * rate;

  const isLoading = overviewLoading || (!asset && overviewLoading);

  // Актив не найден — показываем пустое состояние.
  if (!overviewLoading && overview && !asset) {
    return (
      <Fade in>
        <Box
          sx={{
            marginBottom: "140px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "80px",
            gap: "10px",
          }}
        >
          <Icon icon="solar:danger-triangle-bold-duotone" width={48} height={48} />
          <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
            {t("asset:empty.not_found")}
          </Typography>
          <Typography
            sx={{ fontSize: "13px", color: "text.secondary", textAlign: "center" }}
          >
            {t("asset:empty.not_found_subtitle", { code: upperCode })}
          </Typography>
          <ButtonBase
            onClick={() => navigate("/")}
            sx={{
              marginTop: "8px",
              padding: "10px 22px",
              borderRadius: "40px",
              bgcolor: "primary.main",
              color: "primary.contrastText",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            {t("asset:empty.go_home")}
          </ButtonBase>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in>
      <Box sx={{ width: "100%", marginBottom: "140px", marginTop: "4px" }}>
        {/* -------- Header -------- */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "18px",
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.05)",
              color: "text.primary",
            })}
            aria-label={t("asset:actions.back_aria")}
          >
            <Icon icon="solar:alt-arrow-left-linear" width={22} height={22} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: "center",
              fontSize: "18px",
              fontWeight: 600,
              paddingRight: "40px", // визуальный баланс с кнопкой слева
            }}
          >
            {upperCode}
          </Typography>
        </Box>

        {/* -------- Hero -------- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
            marginBottom: "22px",
          }}
        >
          {isLoading ? (
            <>
              <Skeleton variant="circular" width={72} height={72} />
              <Skeleton
                variant="rounded"
                width={140}
                height={28}
                sx={{ borderRadius: "8px" }}
              />
              <Skeleton
                variant="rounded"
                width={90}
                height={16}
                sx={{ borderRadius: "6px" }}
              />
            </>
          ) : (
            <>
              <CurrencyIcon
                value={asset?.icon ?? DEFAULT_ICONS[upperCode] ?? null}
                size={72}
                fallbackLetter={upperCode}
                alt={upperCode}
                sx={(theme) => ({
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.05)",
                })}
              />
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {formatAmount(amount, asset?.decimals ?? 6)}{" "}
                <Box component="span" sx={{ opacity: 0.6, fontWeight: 500 }}>
                  {upperCode}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                {rubValue > 0 ? `≈ ${formatAmount(rubValue, 0)} ₽` : "0,00 ₽"}
              </Typography>
            </>
          )}
        </Box>

        {/* -------- Actions --------
            Кол-во колонок динамическое: если админ выключил обмен, кнопка
            «Обменять» скрыта, и оставшиеся 3 действия распределяются
            равномерно по всей ширине. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: exchangeEnabled
              ? "1fr 1fr 1fr 1fr"
              : "1fr 1fr 1fr",
            gap: "8px",
            marginBottom: "22px",
          }}
        >
          <AssetActionButton
            icon={<Icon icon="solar:add-circle-bold-duotone" width={26} height={26} />}
            label={t("asset:actions.receive")}
            sublabel={upperCode}
            onClick={handleDeposit}
            disabled={!asset || asset.networks.length === 0}
          />
          <AssetActionButton
            icon={<Icon icon="solar:upload-square-bold-duotone" width={26} height={26} />}
            label={t("asset:actions.withdraw")}
            sublabel={upperCode}
            onClick={handleWithdraw}
            disabled={!asset || asset.networks.length === 0 || amount <= 0}
          />
          {exchangeEnabled && (
            <AssetActionButton
              icon={<Icon icon="solar:refresh-square-bold-duotone" width={26} height={26} />}
              label={t("asset:actions.exchange")}
              sublabel={upperCode}
              onClick={handleExchange}
              disabled={!asset}
            />
          )}
          <AssetActionButton
            icon={<Icon icon="solar:qr-code-bold-duotone" width={26} height={26} />}
            label={t("asset:actions.qr")}
            sublabel={t("asset:actions.qr_sub")}
            onClick={handleQr}
          />
        </Box>

        {/* -------- Wallets per network -------- */}
        {asset && asset.networks.length > 0 && (
          <Box sx={{ marginBottom: "26px" }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "10px",
                paddingLeft: "4px",
              }}
            >
              {t("asset:wallets.section_title", { code: upperCode })}
            </Typography>
            <Box sx={{ display: "grid", gap: "8px" }}>
              {asset.networks.map((n, idx) => {
                const wq = walletQueries[idx];
                const wallet = (wq?.data ?? null) as CryptoWallet | null;
                // Может быть уже в кеше react-query — тогда сразу подхватится.
                const cached = queryClient.getQueryData<CryptoWallet>([
                  "crypto-wallet",
                  n.code,
                  upperCode,
                ]);
                const effectiveWallet = wallet ?? cached ?? null;
                return (
                  <WalletRow
                    key={n.code}
                    currencyCode={upperCode}
                    currencyIcon={asset.icon}
                    network={n}
                    wallet={effectiveWallet}
                    isCreating={creatingNetwork === n.code}
                    isBootstrapping={
                      allWalletsQuery.isLoading || isProvisioning
                    }
                    onOpenQr={() => setQrOpenFor(n.code)}
                    onCreate={() => handleCreate(n.code, idx)}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* -------- History -------- */}
        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "10px",
              paddingLeft: "4px",
            }}
          >
            {t("asset:history.section_title")}
          </Typography>

          <HistoryFilterBar
            dateLabel={dateLabel}
            typeLabel={typeLabel}
            statusLabel={statusLabel}
            dateActive={dateFilter !== null}
            typeActive={typeFilter.length > 0}
            statusActive={statusFilter !== null}
            onDateClick={() => setOpenDrawer("date")}
            onTypeClick={() => setOpenDrawer("type")}
            onStatusClick={() => setOpenDrawer("status")}
          />

          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "24px" }}>
              <CircularProgress size={22} />
            </Box>
          ) : filteredHistory.length === 0 ? (
            <Box
              sx={{
                marginTop: "20px",
                padding: "24px 16px",
                borderRadius: "18px",
                bgcolor: "secondary.main",
                textAlign: "center",
                color: "text.secondary",
                fontSize: "14px",
              }}
            >
              {hasFilters
                ? t("asset:history.empty_filters")
                : t("asset:history.empty_asset", { code: upperCode })}
            </Box>
          ) : (
            <Box sx={{ marginTop: "16px", display: "grid", gap: "18px" }}>
              {groups.map((group) => (
                <Box key={group.key}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "text.secondary",
                      marginBottom: "8px",
                      paddingInline: "4px",
                    }}
                  >
                    {group.label}
                  </Typography>
                  {/* Единая «плитка» на день с hairline-разделителями —
                      иначе строки на глубоко-тёмном фоне сливаются. */}
                  <Box
                    sx={(theme) => ({
                      bgcolor: "background.paper",
                      borderRadius: "18px",
                      padding: "4px 8px",
                      border:
                        theme.palette.mode === "light"
                          ? "1px solid rgba(0, 0, 0, 0.05)"
                          : "none",
                      boxSizing: "border-box",
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    })}
                  >
                    {group.items.map((order, idx) => (
                      <Fragment key={order.id}>
                        {idx > 0 && (
                          <Box
                            sx={{
                              height: "1px",
                              bgcolor: "divider",
                              marginLeft: "48px",
                            }}
                          />
                        )}
                        <HistoryRow order={order} />
                      </Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* -------- Drawers & dialogs -------- */}

        <DateFilterDrawer
          open={openDrawer === "date"}
          value={dateFilter?.from ?? null}
          onClose={closeDrawer}
          onApply={setDateFilter}
        />
        <TypeFilterDrawer
          open={openDrawer === "type"}
          value={typeFilter}
          onClose={closeDrawer}
          onApply={setTypeFilter}
        />
        <StatusFilterDrawer
          open={openDrawer === "status"}
          value={statusFilter}
          onClose={closeDrawer}
          onApply={setStatusFilter}
        />

        <NetworkPickerDrawer
          open={depositPickerOpen}
          onClose={() => setDepositPickerOpen(false)}
          mode="deposit"
          networksOverride={assetNetworks}
          title={
            asset
              ? t("asset:network_picker.title", { code: asset.code })
              : undefined
          }
          subtitle={t("asset:network_picker.subtitle_deposit")}
        />
        <NetworkPickerDrawer
          open={withdrawPickerOpen}
          onClose={() => setWithdrawPickerOpen(false)}
          mode="withdraw"
          networksOverride={assetNetworks}
          title={
            asset
              ? t("asset:network_picker.title", { code: asset.code })
              : undefined
          }
          subtitle={t("asset:network_picker.subtitle_withdraw")}
        />

        {asset &&
          asset.networks.map((n) => {
            const cached = queryClient.getQueryData<CryptoWallet>([
              "crypto-wallet",
              n.code,
              upperCode,
            ]);
            return (
              <WalletQrDialog
                key={n.code}
                open={qrOpenFor === n.code}
                onClose={() => setQrOpenFor(null)}
                currency={upperCode}
                networkName={n.name || n.code}
                networkStandard={n.code}
                address={cached?.address ?? null}
                memo={cached?.memo ?? null}
              />
            );
          })}
      </Box>
    </Fade>
  );
}
